import { Plus, Trash2, X } from 'lucide-react'

import type {
    BranchForm,
    CertificateForm,
    ImageValue,
    StatForm,
    StoreForm,
} from '@/components/retail/home-form-model'
import {
    BRANCH_MAX,
    HERO_POSITIONS,
    OCOP_CERT_MAX,
    STORE_MAX,
    STORY_IMAGE_MAX,
    STORY_STAT_MAX,
    certificateIn,
    emptyBranch,
    emptyStore,
    heroPositionLabel,
    statIn,
} from '@/components/retail/home-form-model'
import {
    ChoiceField,
    PhonesField,
    SortableList,
    SortableRow,
    useArrayField,
} from '@/components/retail/home-form-bits'
import { HomeImageInput } from '@/components/retail/home-image-field'
import { HomeLinkField } from '@/components/retail/home-link-field'
import { ShopImageField } from '@/components/retail/shop-image-field'
import { SwitchField, TextField } from '@/components/retail/form-bits'
import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field'
import { m } from '@/lib/paraglide/messages'

// Ô nhập theo từng khối trang chủ (README 34). `p` là tiền tố đường dẫn dữ liệu
// trong form: "items.N.data" với khối danh sách, "data" với khối một bản —
// trùng params.field của lỗi server.

const req = (label: string) => `${label} *`

/** Ô của một slide đầu trang. */
export function HeroSlideFields({ p }: { p: string }) {
    return (
        <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                    name={`${p}.tab`}
                    label={req(m.shop_home_hero_tab())}
                    hint={m.shop_home_hero_tab_hint()}
                />
                <TextField
                    name={`${p}.tab_sub`}
                    label={m.shop_home_hero_tab_sub()}
                />
                <TextField
                    name={`${p}.badge`}
                    label={m.shop_home_hero_badge()}
                />
                <TextField
                    name={`${p}.badge_mobile`}
                    label={m.shop_home_hero_badge_mobile()}
                />
            </div>
            <TextField
                name={`${p}.title`}
                label={req(m.shop_home_title())}
                hint={m.shop_home_hero_title_hint()}
                rows={2}
            />
            <TextField
                name={`${p}.description`}
                label={m.shop_home_description()}
                rows={3}
            />
            <div className="grid gap-4 sm:grid-cols-2">
                <HomeImageInput
                    name={`${p}.image`}
                    label={m.shop_home_hero_image()}
                    hint={m.shop_home_hero_image_hint()}
                    required
                    className="aspect-video max-w-full"
                />
                <HomeImageInput
                    name={`${p}.image_mobile`}
                    label={m.shop_home_hero_image_mobile()}
                    hint={m.shop_home_hero_image_mobile_hint()}
                    className="aspect-[3/4] max-w-40"
                />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
                <ChoiceField
                    name={`${p}.image_position`}
                    label={m.shop_home_hero_position()}
                    options={HERO_POSITIONS.map((v) => ({
                        value: v,
                        label: heroPositionLabel(v),
                    }))}
                />
                <TextField name={`${p}.image_alt`} label={m.shop_image_alt()} />
                <TextField
                    name={`${p}.cta_label`}
                    label={req(m.shop_home_cta_label())}
                />
                <TextField
                    name={`${p}.cta_label_mobile`}
                    label={m.shop_home_cta_label_mobile()}
                />
            </div>
            <HomeLinkField name={`${p}.link`} label={m.shop_home_link()} />
        </div>
    )
}

/** Ô của một tab dòng chè (chỉ web). */
export function HomeTabFields({ p }: { p: string }) {
    return (
        <div className="space-y-4">
            <TextField name={`${p}.title`} label={req(m.shop_home_title())} />
            <TextField name={`${p}.subtitle`} label={m.shop_home_subtitle()} />
            <HomeLinkField
                name={`${p}.link`}
                label={m.shop_home_link()}
                optional
            />
        </div>
    )
}

/** Ô của một ô khuyến mãi. */
export function PromoFields({ p }: { p: string }) {
    return (
        <div className="space-y-4">
            <HomeImageInput
                name={`${p}.image`}
                label={m.shop_home_image()}
                required
                className="aspect-video max-w-64"
            />
            <div className="grid gap-4 sm:grid-cols-2">
                <TextField name={`${p}.tag`} label={m.shop_home_promo_tag()} />
                <TextField
                    name={`${p}.title`}
                    label={req(m.shop_home_title())}
                />
            </div>
            <TextField name={`${p}.subtitle`} label={m.shop_home_subtitle()} />
            <HomeLinkField
                name={`${p}.link`}
                label={m.shop_home_link()}
                optional
            />
        </div>
    )
}

/** Banner quà biếu: không có ảnh thì banner không hiện. */
export function GiftBannerFields() {
    return (
        <div className="space-y-4">
            <HomeImageInput
                name="data.image"
                label={m.shop_home_image()}
                hint={m.shop_home_gift_image_hint()}
                className="aspect-[3/4] max-w-40"
            />
            <div className="grid gap-4 sm:grid-cols-2">
                <TextField name="data.title" label={m.shop_home_title()} />
                <TextField
                    name="data.subtitle"
                    label={m.shop_home_subtitle()}
                />
                <TextField
                    name="data.cta_label"
                    label={m.shop_home_cta_label()}
                />
            </div>
            <HomeLinkField
                name="data.link"
                label={m.shop_home_link()}
                optional
            />
            <fieldset className="space-y-3 rounded-md border p-3">
                <legend className="px-1 text-sm font-medium">
                    {m.shop_home_gift_mobile()}
                </legend>
                <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                        name="data.mobile_title"
                        label={m.shop_home_title()}
                    />
                    <TextField
                        name="data.mobile_subtitle"
                        label={m.shop_home_subtitle()}
                    />
                    <TextField
                        name="data.mobile_cta_label"
                        label={m.shop_home_cta_label()}
                    />
                </div>
            </fieldset>
        </div>
    )
}

/** Khối câu chuyện (chỉ web): chữ, tối đa 3 ảnh, tối đa 3 số liệu. */
export function StoryFields({ canWrite }: { canWrite: boolean }) {
    const [images, setImages] = useArrayField<ImageValue>('data.images')
    const [stats, setStats] = useArrayField<StatForm>('data.stats')
    return (
        <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                    name="data.eyebrow"
                    label={m.shop_home_story_eyebrow()}
                />
                <TextField name="data.title" label={m.shop_home_title()} />
            </div>
            <TextField
                name="data.body"
                label={m.shop_home_story_body()}
                rows={5}
            />
            <div className="space-y-2">
                <p className="text-sm font-medium">
                    {m.shop_home_story_images({
                        max: String(STORY_IMAGE_MAX),
                    })}
                </p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {images.map((img, i) => (
                        <div key={img.url + String(i)}>
                            <ShopImageField
                                asObject
                                id={`data.images.${i}`}
                                label={m.shop_home_story_image_n({
                                    n: String(i + 1),
                                })}
                                value={img}
                                onChange={(v) =>
                                    setImages(
                                        v
                                            ? images.map((x, j) =>
                                                  j === i ? v : x,
                                              )
                                            : images.filter((_, j) => j !== i),
                                    )
                                }
                            />
                            <FieldError name={`data.images.${i}`} />
                        </div>
                    ))}
                    {canWrite && images.length < STORY_IMAGE_MAX && (
                        <ShopImageField
                            asObject
                            id="data.images.new"
                            label={m.shop_home_story_image_add()}
                            value={null}
                            onChange={(v) => v && setImages([...images, v])}
                        />
                    )}
                </div>
                <FieldError name="data.images" />
            </div>
            <div className="space-y-2">
                <p className="text-sm font-medium">
                    {m.shop_home_story_stats({ max: String(STORY_STAT_MAX) })}
                </p>
                {stats.map((s, i) => (
                    <div
                        key={s.key}
                        className="grid grid-cols-[1fr_2fr_auto] items-start gap-2"
                    >
                        <TextField
                            name={`data.stats.${i}.value`}
                            label={req(m.shop_home_stat_value())}
                        />
                        <TextField
                            name={`data.stats.${i}.label`}
                            label={req(m.shop_home_stat_label())}
                        />
                        {canWrite && (
                            <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="mt-6"
                                aria-label={m.shop_home_stat_remove_aria({
                                    n: String(i + 1),
                                })}
                                onClick={() =>
                                    setStats(stats.filter((_, j) => j !== i))
                                }
                            >
                                <X className="size-4" />
                            </Button>
                        )}
                    </div>
                ))}
                {canWrite && stats.length < STORY_STAT_MAX && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setStats([...stats, statIn({})])}
                    >
                        <Plus className="mr-1 size-4" />
                        {m.shop_home_stat_add()}
                    </Button>
                )}
                <FieldError name="data.stats" />
            </div>
            <TextField name="data.cta_label" label={m.shop_home_cta_label()} />
            <HomeLinkField
                name="data.link"
                label={m.shop_home_link()}
                optional
            />
        </div>
    )
}

/** Khối OCOP: tiêu đề, logo, tối đa 12 giấy chứng nhận (kéo thả). */
export function OcopFields({ canWrite }: { canWrite: boolean }) {
    const [certs, setCerts] =
        useArrayField<CertificateForm>('data.certificates')
    return (
        <div className="space-y-4">
            <TextField name="data.title" label={m.shop_home_title()} />
            <TextField
                name="data.subtitle"
                label={m.shop_home_subtitle()}
                rows={2}
            />
            <HomeImageInput
                name="data.logo"
                label={m.shop_home_ocop_logo()}
                className="max-w-24"
            />
            <div className="space-y-2">
                <p className="text-sm font-medium">
                    {m.shop_home_ocop_certificates({
                        max: String(OCOP_CERT_MAX),
                    })}
                </p>
                <SortableList rows={certs} onChange={setCerts}>
                    {certs.map((c, i) => {
                        const cp = `data.certificates.${i}`
                        return (
                            <SortableRow
                                key={c.key}
                                id={c.key}
                                label={c.title || String(i + 1)}
                                canWrite={canWrite}
                                className="rounded-md border p-3"
                            >
                                <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
                                    <HomeImageInput
                                        name={`${cp}.image`}
                                        label={m.shop_home_image()}
                                        required
                                        className="max-w-28"
                                    />
                                    <div className="space-y-3">
                                        <TextField
                                            name={`${cp}.title`}
                                            label={req(m.shop_home_title())}
                                        />
                                        <div className="grid gap-3 sm:grid-cols-2">
                                            <ChoiceField
                                                name={`${cp}.stars`}
                                                label={m.shop_ocop_stars()}
                                                noneLabel="—"
                                                options={['3', '4', '5'].map(
                                                    (n) => ({
                                                        value: n,
                                                        label: m.shop_ocop_stars_n(
                                                            { n },
                                                        ),
                                                    }),
                                                )}
                                            />
                                            <ChoiceField
                                                name={`${cp}.level`}
                                                label={m.shop_home_ocop_level()}
                                                noneLabel="—"
                                                options={[
                                                    {
                                                        value: 'provincial',
                                                        label: m.shop_home_ocop_level_provincial(),
                                                    },
                                                    {
                                                        value: 'national',
                                                        label: m.shop_home_ocop_level_national(),
                                                    },
                                                ]}
                                            />
                                        </div>
                                        {canWrite && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="ghost"
                                                className="text-destructive"
                                                onClick={() =>
                                                    setCerts(
                                                        certs.filter(
                                                            (_, j) => j !== i,
                                                        ),
                                                    )
                                                }
                                            >
                                                <Trash2 className="mr-1 size-4" />
                                                {m.shop_home_remove_row()}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </SortableRow>
                        )
                    })}
                </SortableList>
                {canWrite && certs.length < OCOP_CERT_MAX && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setCerts([...certs, certificateIn({})])}
                    >
                        <Plus className="mr-1 size-4" />
                        {m.shop_home_ocop_add()}
                    </Button>
                )}
                <FieldError name="data.certificates" />
            </div>
        </div>
    )
}

/** Hệ thống cửa hàng: trụ sở + tối đa 12 chi nhánh × 30 cửa hàng (kéo thả). */
export function StoreSystemFields({ canWrite }: { canWrite: boolean }) {
    const [branches, setBranches] = useArrayField<BranchForm>('data.branches')
    return (
        <div className="space-y-4">
            <fieldset className="space-y-3 rounded-md border p-3">
                <legend className="px-1 text-sm font-medium">
                    {m.shop_home_hq()}
                </legend>
                <TextField
                    name="data.headquarters.title"
                    label={m.shop_home_title()}
                />
                <TextField
                    name="data.headquarters.address"
                    label={m.shop_home_address()}
                    rows={2}
                />
                <TextField
                    name="data.headquarters.map_url"
                    label={m.shop_home_map_url()}
                    hint={m.shop_home_http_hint()}
                />
                <PhonesField
                    name="data.headquarters.phones"
                    label={m.shop_home_phones()}
                    canWrite={canWrite}
                />
                <HomeImageInput
                    name="data.headquarters.image"
                    label={m.shop_home_image()}
                    className="aspect-video max-w-56"
                />
            </fieldset>
            <div className="space-y-2">
                <p className="text-sm font-medium">
                    {m.shop_home_branches({ max: String(BRANCH_MAX) })}
                </p>
                <SortableList rows={branches} onChange={setBranches}>
                    {branches.map((b, i) => (
                        <SortableRow
                            key={b.key}
                            id={b.key}
                            label={b.name || String(i + 1)}
                            canWrite={canWrite}
                            className="rounded-md border p-3"
                        >
                            <BranchFields
                                index={i}
                                canWrite={canWrite}
                                onRemove={() =>
                                    setBranches(
                                        branches.filter((_, j) => j !== i),
                                    )
                                }
                            />
                        </SortableRow>
                    ))}
                </SortableList>
                {canWrite && branches.length < BRANCH_MAX && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                            setBranches([...branches, emptyBranch()])
                        }
                    >
                        <Plus className="mr-1 size-4" />
                        {m.shop_home_branch_add()}
                    </Button>
                )}
                <FieldError name="data.branches" />
            </div>
        </div>
    )
}

function BranchFields({
    index,
    canWrite,
    onRemove,
}: {
    index: number
    canWrite: boolean
    onRemove: () => void
}) {
    const bp = `data.branches.${index}`
    const [stores, setStores] = useArrayField<StoreForm>(`${bp}.stores`)
    return (
        <div className="space-y-3">
            <TextField
                name={`${bp}.name`}
                label={req(m.shop_home_branch_name())}
            />
            <div className="flex flex-wrap gap-4">
                <SwitchField
                    name={`${bp}.show_web`}
                    label={m.shop_show_web()}
                />
                <SwitchField
                    name={`${bp}.show_miniapp`}
                    label={m.shop_show_miniapp()}
                />
            </div>
            <p className="text-sm font-medium">
                {m.shop_home_stores({ max: String(STORE_MAX) })}
            </p>
            <SortableList rows={stores} onChange={setStores}>
                {stores.map((s, j) => (
                    <SortableRow
                        key={s.key}
                        id={s.key}
                        label={s.name || String(j + 1)}
                        canWrite={canWrite}
                        className="rounded-md bg-muted/40 p-3"
                    >
                        <StoreFields
                            sp={`${bp}.stores.${j}`}
                            canWrite={canWrite}
                            onRemove={() =>
                                setStores(stores.filter((_, k) => k !== j))
                            }
                        />
                    </SortableRow>
                ))}
            </SortableList>
            <FieldError name={`${bp}.stores`} />
            {canWrite && (
                <div className="flex flex-wrap gap-2">
                    {stores.length < STORE_MAX && (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setStores([...stores, emptyStore()])}
                        >
                            <Plus className="mr-1 size-4" />
                            {m.shop_home_store_add()}
                        </Button>
                    )}
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={onRemove}
                    >
                        <Trash2 className="mr-1 size-4" />
                        {m.shop_home_branch_remove()}
                    </Button>
                </div>
            )}
        </div>
    )
}

function StoreFields({
    sp,
    canWrite,
    onRemove,
}: {
    sp: string
    canWrite: boolean
    onRemove: () => void
}) {
    return (
        <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
                <TextField
                    name={`${sp}.name`}
                    label={req(m.shop_home_store_name())}
                />
                <TextField
                    name={`${sp}.website`}
                    label={m.shop_home_website()}
                    hint={m.shop_home_http_hint()}
                />
            </div>
            <TextField
                name={`${sp}.address`}
                label={req(m.shop_home_address())}
                rows={2}
            />
            <TextField
                name={`${sp}.map_url`}
                label={m.shop_home_map_url()}
                hint={m.shop_home_http_hint()}
            />
            <PhonesField
                name={`${sp}.phones`}
                label={m.shop_home_phones()}
                canWrite={canWrite}
            />
            <div className="flex flex-wrap items-center gap-4">
                <SwitchField
                    name={`${sp}.show_web`}
                    label={m.shop_show_web()}
                />
                <SwitchField
                    name={`${sp}.show_miniapp`}
                    label={m.shop_show_miniapp()}
                />
                {canWrite && (
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="ml-auto text-destructive"
                        onClick={onRemove}
                    >
                        <Trash2 className="mr-1 size-4" />
                        {m.shop_home_store_remove()}
                    </Button>
                )}
            </div>
        </div>
    )
}
